import { Fragment, ReactNode, useEffect, useState } from 'react';
import { CartItem } from '../../clients/customer-api/cart/cart';
import { UserAddress } from '../../clients/quipli-service/user/user';
import { Typography } from '../../components/typography/typography';
import { useCustomer } from '../../hooks/use-customer-context';
import { AccountAddresses } from '../account/addresses/account-addresses';
import { CheckoutStep } from './checkout-step';
import { StepAction } from './checkout-view';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { ProductImage } from '../../components/image/product/product-image';
import { ProductImageV2 } from '../../components/image/product/product-image-v2';
import { SelectForm } from '../../components/select-form/select-form';
import { Button, EcommerceButton } from '../../components/form/button';
import Icon from '../../components/icons/icon';
import { Location } from '../../clients/quipli-service/location/location';
import { DeliveryOption, ShippingType } from '../../clients/quipli-service/delivery/delivery';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { Divider } from '../../components/divider/divider';
import { ecommerceToast } from '../../util/toasts';
import { CheckoutSelectedAddress } from './checkout-selected-address';
import { components } from 'react-select';

export const CheckoutDelivery = ({
  title,
  step,
  currentStep,
  addresses,
  cartItems,
  location,
  reload,
  setCurrentstep,
  onSelectAddress,
  selectedAddress,
  reloadAddresses,
  isSubmitting,
  isQuote = false,
  showSelectedAddress = false,
  selectedDelivery,
  setSelectedDelivery,
  useImageV2,
  requireDeliveryAddressForPickup = false,
}: {
  title: string;
  step: number;
  currentStep: StepAction;
  addresses: UserAddress[];
  cartItems: CartItem[];
  location: Location;
  reload?: () => void;
  setCurrentstep: (step: StepAction) => void;
  onSelectAddress?: (address?: UserAddress) => void;
  selectedAddress?: UserAddress;
  reloadAddresses?: () => Promise<void>;
  isSubmitting: boolean;
  isQuote?: boolean;
  showSelectedAddress?: boolean;
  selectedDelivery?: DeliveryOption;
  setSelectedDelivery: (delivery?: DeliveryOption) => void;
  useImageV2: boolean;
  requireDeliveryAddressForPickup: boolean;
}) => {
  const customer = useCustomer();
  const { getDeliveryOptions, removeItemFromCart } = useCustomerAPI();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [modal, setModal] = useState<ReactNode>();

  const confirmDelivery = async () => {
    if (selectedDelivery === undefined) {
      ecommerceToast('Please select a delivery option.', 'error');
      return;
    }

    if (!requireDeliveryAddressForPickup && selectedDelivery.type === ShippingType.SelfPickUp) {
      setCurrentstep(StepAction.Billing);
      onSelectAddress?.(undefined);
    } else {
      setCurrentstep(StepAction.DeliveryAddress);
    }
  };

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);

      try {
        const _options = await getDeliveryOptions(
          cartItems.map((item) => ({ id: item.product.id, type: item.type })),
          location.id,
        );

        setDeliveryOptions(_options);
      } catch (e) {
        ecommerceToast('Failed to load delivery options.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
  }, []);

  return (
    <CheckoutStep
      title={title}
      step={step}
      contentPadding="p-0"
      isCompleted={currentStep > StepAction.DeliveryAddress}
      isEditDisabled={isSubmitting || isQuote}
      hideEdit={isQuote}
      onEdit={() => setCurrentstep(StepAction.Shipping)}
    >
      {currentStep >= StepAction.Shipping && customer && (
        <>
          {!isQuote && (
            <div className="grid grid-cols-1 sm:grid-cols-2">
              <div className="bg-grey-50 p-3 hidden sm:inline-grid">
                <Typography type="text-xs" weight="semi-bold">
                  Product
                </Typography>
              </div>
              <div className="bg-grey-50 p-3 hidden sm:inline-grid">
                <Typography type="text-xs" weight="semi-bold">
                  Delivery
                </Typography>
              </div>

              {cartItems
                .sort((a, b) => (a.id > b.id ? 1 : b.id > a.id ? -1 : 0))
                .map((item) => {
                  return (
                    <Fragment key={`cart-item-${item.id}`}>
                      <div className="p-3 flex gap-2 items-center">
                        <div>
                          {useImageV2 ? (
                            <ProductImageV2
                              className="max-w-[70px] max-h-[70px] rounded-lg overflow-hidden border border-solid border-grey-100"
                              productId={item.product.id}
                              profile="MEDIUM"
                              alt={item.product.name}
                            />
                          ) : (
                            <ProductImage
                              className="max-w-[70px] max-h-[70px] rounded-lg overflow-hidden border border-solid border-grey-100"
                              productId={item.product.id}
                              profile="THUMB"
                            />
                          )}
                        </div>

                        <Typography type="text-sm" weight="semi-bold" dataCy={`cart-item-name-${item.id}`}>
                          {item.product.name}
                        </Typography>
                      </div>

                      <div className="p-3 flex flex-col gap-2 justify-center">
                        <div className="flex items-center w-full justify-between">
                          {selectedDelivery && selectedDelivery.descriptions && (
                            <ProductDeliveryDescription
                              productId={item.product.id}
                              descriptions={selectedDelivery.descriptions}
                            />
                          )}
                          {currentStep <= StepAction.DeliveryAddress && (
                            <div className="grow flex justify-end">
                              <Button
                                className="icon-button !pr-0"
                                data-cy={`cart-item-remove-${item.id}`}
                                onClick={() =>
                                  !isSubmitting &&
                                  setModal(
                                    <ConfirmModal
                                      prompt="Remove item?"
                                      additionalText="This cannot be undone"
                                      noLabel="Cancel"
                                      yesLabel="Remove"
                                      yesButtonYellow
                                      isOpen
                                      useEcommerceTheme
                                      yesDataCy="cart-item-remove"
                                      headerIcon={
                                        <div className="flex justify-center">
                                          <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                                        </div>
                                      }
                                      onConfirm={async () => {
                                        await removeItemFromCart(item.id, location.id);
                                        setModal(undefined);
                                        reload?.();
                                      }}
                                      close={() => setModal(undefined)}
                                    />,
                                  )
                                }
                              >
                                <Icon icon="Trash" stroke="var(--grey-400)" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-span-2 py-2">
                        <Divider />
                      </div>
                    </Fragment>
                  );
                })}
            </div>
          )}

          {currentStep === StepAction.Shipping && (
            <div className="flex justify-between gap-4 p-3">
              <CheckoutDeliverySelection
                isLoading={isLoading}
                options={deliveryOptions}
                selectedDelivery={selectedDelivery}
                setSelectedDelivery={setSelectedDelivery}
              />

              <div className="self-start">
                <EcommerceButton dataCy="delivery_confirm" onClick={confirmDelivery}>
                  Confirm
                </EcommerceButton>
              </div>
            </div>
          )}

          {currentStep >= StepAction.DeliveryAddress &&
            (selectedDelivery?.type !== ShippingType.SelfPickUp || requireDeliveryAddressForPickup) &&
            (showSelectedAddress ? (
              <CheckoutSelectedAddress address={selectedAddress} />
            ) : (
              <AccountAddresses
                addresses={addresses}
                enableSelect={true}
                type="delivery"
                onSelect={(value) => {
                  if (isSubmitting) return;

                  currentStep === StepAction.DeliveryAddress &&
                    addresses.length > 0 &&
                    setCurrentstep(StepAction.Billing);
                  onSelectAddress?.(value);
                }}
                onOpenForm={() => {
                  if (isSubmitting) return;

                  setCurrentstep(StepAction.DeliveryAddress);
                }}
                selectedAddress={selectedAddress}
                reload={reloadAddresses}
                location={location}
              />
            ))}

          {modal}
        </>
      )}
    </CheckoutStep>
  );
};

const CheckoutDeliverySelection = ({
  isLoading,
  options,
  selectedDelivery,
  setSelectedDelivery,
}: {
  isLoading: boolean;
  options: DeliveryOption[];
  selectedDelivery?: DeliveryOption;
  setSelectedDelivery: (delivery?: DeliveryOption) => void;
}) => {
  return (
    <div className="w-full sm:w-1/2 flex flex-col gap-2">
      <SelectForm
        placeholder="Select delivery option"
        isLoading={isLoading}
        isSearchable={false}
        dataCy="delivery-option-select"
        options={options}
        value={selectedDelivery}
        getOptionValue={(o) => `${o.type}`}
        onChange={(o) => setSelectedDelivery(o || undefined)}
        components={{
          Option: (props) => (
            <components.Option {...props}>
              <div className="flex items-center gap-[6px] justify-between">
                <div className="flex flex-col">
                  <Typography
                    type="text-sm"
                    dataCy={`option-label-${props.data.label}`}
                    className="text-quipli-black-700"
                  >
                    {props.data.label}
                  </Typography>
                  <Typography
                    type="text-sm"
                    dataCy={`option-additional-text-${props.data.label}`}
                    className="text-quipli-black-500"
                  >
                    {props.data.additionalText}
                  </Typography>
                </div>

                {props.isSelected && <Icon icon="Check" stroke="var(--quipli-black-800)" />}
              </div>
            </components.Option>
          ),
        }}
        classNamePrefix="delivery_option"
      />

      {selectedDelivery?.additionalText && (
        <Typography type="text-sm" className="text-grey-500">
          {selectedDelivery.additionalText}
        </Typography>
      )}
    </div>
  );
};

const ProductDeliveryDescription = ({
  productId,
  descriptions,
}: {
  productId: number;
  descriptions: (DeliveryOption & { productId: number })[];
}) => {
  const desc = descriptions.find((d) => d.productId === productId);

  return desc ? (
    <div className="">
      <Typography type="text-sm" className="text-quipli-black-500">
        {desc.label}
      </Typography>
    </div>
  ) : null;
};
